import React, { useEffect, useState } from "react";
import {
    Card,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Avatar
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getAllCustomers } from "../services/customers";
import { DataGrid } from "@mui/x-data-grid";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

const StyledCard = styled(Card)({
    background: "#ffffff",
    borderRadius: "20px",
    padding: "20px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
});

const BlueHeader = styled(TableHead)({
    "& th": {
        backgroundColor: "#4A90E2",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "14.5px",
        padding: "14px",
        borderBottom: "none",
    },
    "& th:first-of-type": {
        borderTopLeftRadius: "12px",
    },
    "& th:last-of-type": {
        borderTopRightRadius: "12px",
    },
});

const StyledRow = styled(TableRow)({
    "&:hover": {
        backgroundColor: "#f5f8ff",
        cursor: "pointer",
    },
});

const StyledCell = styled(TableCell)({
    padding: "14px",
    fontSize: "14px",
    color: "#333",
    borderBottom: "1px solid #eee",
});

const ScrollBox = styled(Box)({
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    "&::-webkit-scrollbar": {
        height: 8,
    },
    "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
        borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "#4a90e2",
        borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "#357ABD",
    },
});

const StyledDataGrid = styled(DataGrid)({
    border: "none",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    minWidth: 1200,
    width: "100%",
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#4a90e2",
    },
    "& .MuiDataGrid-columnHeader": {
        backgroundColor: "#4a90e2",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.9rem",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontSize: "0.8rem",
        whiteSpace: "normal",
        lineHeight: 1.2,
    },
    "& .MuiDataGrid-columnSeparator": {
        color: "rgba(255, 255, 255, 0.3)",
    },
    "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
        color: "#ffffff",
    },
    "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root, & .MuiDataGrid-cellCheckbox .MuiCheckbox-root": {
        transform: "scale(0.8)",
    },
    "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
        color: "#ffffff",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.95rem",
        borderRight: "none",
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
        outline: "none",
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "rgba(74, 144, 226, 0.05)",
        cursor: "pointer",
    },
    "& .MuiDataGrid-row.Mui-selected": {
        backgroundColor: "rgba(74, 144, 226, 0.1)",
    },
    "& .MuiDataGrid-row.Mui-selected:hover": {
        backgroundColor: "rgba(74, 144, 226, 0.15)",
    },
});

// same-day check, format agnostic
const isToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    );
};

export default function TodaysPatients() {
    const navigate = useNavigate();
    const accountId = localStorage.getItem("account_id");
    const [customers, setCustomers] = useState([]);

    const fetchCustomers = async (query = "") => {
        try {
            if (!accountId) {
                console.error("Account ID not found in localStorage");
                return;
            }

            const data = await getAllCustomers(accountId, query);
            const allCustomers = data.customers || [];

            // filter to today only
            const filteredCustomers = allCustomers.filter((c) => isToday(c.updated_at));

            setCustomers(filteredCustomers);

        } catch (err) {
            console.log(err, "Failed to fetch customers");
        }
    };

    useEffect(() => {
        fetchCustomers();

    }, []);

    const getGenderAge = ({ gender, date_of_birth, age }) => {
        const calculateAge = (dob) => {
            if (!dob) return null;
            const birthDate = new Date(dob);
            const today = new Date();
            let a = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) a--;
            return a;
        };

        const displayAge = age || calculateAge(date_of_birth);

        return gender && displayAge
            ? `${gender[0].toUpperCase() + gender.slice(1)} - ${displayAge} years`
            : gender
                ? gender[0].toUpperCase() + gender.slice(1)
                : displayAge
                    ? `${displayAge} years`
                    : "—";
    };

    const columns = [
        {
            field: "name",
            headerName: "Name",
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => {
                const { firstname, lastname } = params.row;
                const fullName = `${firstname || ""} ${lastname || ""}`.trim();
                const genderAge = getGenderAge(params.row);

                return (
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                        <Avatar sx={{ width: 30, height: 30, bgcolor: "primary.main", mr: 1.5 }}>
                            {fullName?.split(" ").map((n) => n[0]).join("").toUpperCase()}
                        </Avatar>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                {fullName || "—"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#111827" }}>
                                {genderAge}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: "personal_info",
            headerName: "Personal Info",
            flex: 2,
            minWidth: 200,
            renderCell: ({ row }) => (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                        <PhoneIcon sx={{ fontSize: "1rem", color: "#4b5563" }} />
                        <Typography variant="body2">{row.phone || "—"}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                        <EmailIcon sx={{ fontSize: "1rem", color: "#4b5563" }} />
                        <Typography variant="body2">{row.email || "—"}</Typography>
                    </Box>
                </Box>
            ),
        },
        {
            field: "assigned_doctor_name",
            headerName: "Assigned Doctor",
            flex: 1.5,
            minWidth: 150,
            renderCell: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                    }}
                >
                    <Typography>{params.value || "—"}</Typography>
                </Box>
            ),
        },
    ];


    return (
        <StyledCard>
            {/* ---------- Header row: Title (left) + Back (right) ---------- */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",        // vertical center alignment
                    justifyContent: "space-between", // push title left, button right
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",           // keeps layout responsive on very small screens
                }}
            >
                {/* LEFT: your exact text block (kept verbatim) */}
                <Box sx={{ flex: { xs: "1 1 auto", sm: "0 0 auto" }, display: "flex", gap: 2, alignItems: "center" }}>
                    <Typography
                        variant="h6"
                        fontWeight={400}
                        sx={{
                            color: "#1f2937",
                            mb: 0,
                            fontSize: { xs: "1.0rem", sm: "1.25rem" },
                            whiteSpace: "nowrap",
                        }}
                    >
                        Today Appointment
                    </Typography>
                </Box>

                {/* RIGHT: Back button (keeps to the right) */}
                <Box sx={{ flex: { xs: "0 0 auto", sm: "0 0 auto" }, display: "flex", alignItems: "center" }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            borderColor: "#0B2D6B",
                            color: "#0B2D6B",
                            px: 2,
                            height: 36,
                        }}
                    >
                        Back
                    </Button>
                </Box>
            </Box>

            {/* ---------- Table ---------- */}
            {/* <TableContainer
                sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    background: "#ffffff",
                }}
            >
                <Table>
                    <BlueHeader>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Personal Info</TableCell>
                            <TableCell>Assigned Doctor</TableCell>
                            <TableCell>Progress Tracking</TableCell>
                        </TableRow>
                    </BlueHeader>

                    <TableBody>
                        {customers.length === 0 ? (
                            <StyledRow>
                                <StyledCell colSpan={5} align="center">
                                    No patients found
                                </StyledCell>
                            </StyledRow>
                        ) : (
                            customers.map((customer) => (
                                <StyledRow key={customer.id}>
                                    <TableCell>
                                        {customer.firstname} {customer.lastname}
                                    </TableCell>

                                    <TableCell>
                                        <div><strong>DOB:</strong> {customer.date_of_birth?.split("T")[0]}</div>
                                        <div><strong>Age:</strong> {customer.age}</div>
                                        <div><strong>Gender:</strong> {customer.gender}</div>
                                        <div><strong>Email:</strong> {customer.email}</div>
                                        <div><strong>Phone:</strong> {customer.phone}</div>
                                    </TableCell>

                                    <TableCell>
                                        {customer.assigned_doctor_name || "NA"}
                                    </TableCell>

                                    <TableCell>
                                        <div><strong>Diagnosis:</strong> {customer.diagnosis}</div>
                                        <div><strong>Primary:</strong> {customer.primary_diagnosis}</div>
                                        <div><strong>Stage:</strong> {customer.stage}</div>
                                        <div><strong>Treatment:</strong> {customer.treatment_type}</div>
                                    </TableCell>
                                </StyledRow>
                            ))
                        )}
                    </TableBody>

                </Table>
            </TableContainer> */}
            <ScrollBox>
                <StyledDataGrid
                    rows={customers}
                    columns={columns}
                    getRowId={(row) => row.id}
                    disableRowSelectionOnClick
                    rowHeight={70}
                />
            </ScrollBox>
        </StyledCard>
    );
}